import React from "react";
import { useAvailableProviders } from "../../../hooks/useAiAgents";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../../../hooks/useUiLocale";
import {
  resolveSchemaDescription,
  resolveSchemaTitle,
} from "../../../lib/aiAgentI18n";

const ProviderSchemasInfo = () => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  const { data: availableProviders, isLoading } = useAvailableProviders();

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800 p-3 sm:p-6">
        <div className="animate-pulse space-y-3 sm:space-y-4">
          <div className="h-4 w-1/4 rounded bg-gray-700 sm:h-5"></div>
          <div className="space-y-2 sm:space-y-3">
            <div className="h-3 w-3/4 rounded bg-gray-700 sm:h-4"></div>
            <div className="h-3 w-1/2 rounded bg-gray-700 sm:h-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!availableProviders || availableProviders.length === 0) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800 p-3 sm:p-6">
        <p className="text-sm text-gray-400 sm:text-base">
          {t("schemas.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-3 sm:p-6">
      <h3 className="mb-3 text-base font-semibold text-white sm:mb-4 sm:text-lg">
        {t("schemas.title")}
      </h3>
      <div className="space-y-3 sm:space-y-4">
        {availableProviders.map((provider) => (
          <div
            key={provider.provider}
            className="rounded-lg border border-gray-700 p-3 sm:p-4"
          >
            <h4 className="sm:text-md mb-2 text-sm font-medium text-white capitalize sm:mb-3">
              {provider.provider}
            </h4>
            <div className="space-y-2 sm:space-y-3">
              {provider.types.map((type) => (
                <div
                  key={type.type}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0"
                >
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs text-gray-300 sm:text-sm">
                      {resolveSchemaTitle({
                        title: type.title,
                        provider: provider.provider,
                        type: type.type,
                        t,
                        locale,
                      })}
                    </span>
                    {type.description && (
                      <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                        {resolveSchemaDescription({
                          description: type.description,
                          provider: provider.provider,
                          type: type.type,
                          t,
                          locale,
                        })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-end sm:justify-start">
                    {type.hasSettings ? (
                      <span className="rounded-full border border-green-500/30 bg-green-500/20 px-2 py-1 text-xs whitespace-nowrap text-green-400 sm:px-3">
                        {t("schemas.badges.configurable")}
                      </span>
                    ) : (
                      <span className="rounded-full border border-gray-500/30 bg-gray-500/20 px-2 py-1 text-xs whitespace-nowrap text-gray-400 sm:px-3">
                        {t("schemas.badges.notAvailable")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProviderSchemasInfo;
